import React, { useEffect, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { CheckCircle2, Loader2, Mail, ArrowRight } from "lucide-react";
import { api } from "../api";

export default function CheckoutSuccess() {
  const [params] = useSearchParams();
  const orderId = params.get("order");
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    if (!orderId) { setLoading(false); return; }
    let attempts = 0;
    const tick = async () => {
      attempts += 1;
      try {
        const { data } = await api.get(`/orders/${orderId}`);
        if (cancelled) return;
        setOrder(data);
        // Stop polling once payment is verified, or after 6 attempts (~30 s)
        if (data.payment_verified || attempts >= 6) {
          setLoading(false);
          return;
        }
        setTimeout(tick, 5000);
      } catch (e) {
        if (cancelled) return;
        setLoading(false);
      }
    };
    tick();
    return () => { cancelled = true; };
  }, [orderId]);

  return (
    <div className="max-w-3xl mx-auto px-4 py-16" data-testid="checkout-success-page">
      <div className="glass rounded-3xl p-10 text-center border-emerald-400/40">
        <div className="w-16 h-16 mx-auto mb-6 rounded-full bg-emerald-400/10 flex items-center justify-center">
          {loading ? (
            <Loader2 className="h-8 w-8 text-emerald-400 animate-spin" />
          ) : (
            <CheckCircle2 className="h-8 w-8 text-emerald-400" />
          )}
        </div>
        <h1 className="text-3xl sm:text-4xl font-bold mb-3">
          {loading ? "Confirmation du paiement…" : "Paiement reçu — merci !"}
        </h1>
        <p className="text-zinc-300 leading-relaxed mb-8 max-w-xl mx-auto">
          {loading
            ? "CinetPay nous a redirigé. Nous vérifions le statut de votre paiement…"
            : order?.payment_verified
            ? "Votre paiement est confirmé. Notre équipe va personnaliser votre licence et vous l'envoyer par email sous quelques heures."
            : "Votre paiement est en cours de validation auprès de CinetPay. Vous recevrez un email dès la confirmation finale (max 5 min)."}
        </p>

        {order && (
          <div className="bg-white/[0.03] border border-white/[0.08] rounded-2xl p-6 text-left mb-8 text-sm space-y-1.5">
            <div><span className="text-zinc-500">Commande&nbsp;:</span> <code className="text-cyan-300">#{order.id?.slice(0, 8)}</code></div>
            <div><span className="text-zinc-500">Plan&nbsp;:</span> {order.plan_label}</div>
            <div><span className="text-zinc-500">Montant&nbsp;:</span> {order.price_display}</div>
            <div><span className="text-zinc-500">Email de livraison&nbsp;:</span> {order.email}</div>
            {order.cinetpay_transaction_id && (
              <div><span className="text-zinc-500">Référence CinetPay&nbsp;:</span> <code className="text-xs">{order.cinetpay_transaction_id}</code></div>
            )}
          </div>
        )}

        <div className="flex flex-col sm:flex-row gap-3 justify-center">
          {orderId && (
            <Link to={`/track/${orderId}`} className="btn-primary inline-flex items-center justify-center gap-2 px-5 py-3 rounded-lg text-sm">
              Suivre ma commande <ArrowRight className="h-4 w-4" />
            </Link>
          )}
          <a href="mailto:contact@drtradebots.com" className="btn-ghost inline-flex items-center justify-center gap-2 px-5 py-3 rounded-lg text-sm">
            <Mail className="h-4 w-4" /> Contacter le support
          </a>
        </div>
      </div>
    </div>
  );
}
